import { Component, OnInit, ChangeDetectionStrategy, signal, computed, effect, DestroyRef } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { JsonDelivery, Delivery, JsonDeliveryHole, JsonDeliveryDeto } from 'src/app/bo/delivery';
import { UpdateDelivery, UpdateHoleDelivery } from 'src/app/bo/update-delivery';
import { ProductType } from 'src/app/bo/products/productType';
import { BlastService } from 'src/app/services/blast.service';
import { DeliveriesService } from 'src/app/services/deliveries.service';
import { FonctionsService } from 'src/app/services/fonctions-partagees.service';
import { DeliveryParams, HoleService, HoleCondition } from 'src/app/services/hole.service';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-delivery-modale',
  templateUrl: './delivery-modale.component.html',
  styleUrls: ['./delivery-modale.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DeliveryModaleComponent implements OnInit {

  protected delivery: Delivery = new Delivery();
  protected jsonDelivery: JsonDelivery = new JsonDelivery();
  protected destinationBlast = signal<{ blastId: number, blastName: string }[]>([]);
  protected destBlastId = signal<number | null>(null);
  protected destBlastname = signal<string>('');
  protected destDeliveryName = signal<string>('');
  protected currentSiteId = signal<number>(0);
  protected holesDeliveryProxy = signal<{ hole: JsonDeliveryHole, selected: boolean }[]>([]);
  public onClose: Subject<boolean> = new Subject();

  reaffectDeliveryToggled = signal(false);
  splitDeliveryToggled = signal(false);
  modifyLoadForm: FormGroup = new FormGroup({});
  selectedTab = signal<string>('delivery');
  deliveryParams = signal<DeliveryParams | undefined>(undefined);

  protected allHoles = signal<JsonDeliveryHole[]>([]);
  protected visibleHoles = signal<JsonDeliveryHole[]>([]);
  protected pageSize = signal<number>(5);
  protected currentLoadedCount = signal<number>(0);
  protected isLoading = signal<boolean>(false);
  private scrollTimeout?: ReturnType<typeof setTimeout>;


  constructor(
    private readonly bsModalRef: BsModalRef,
    private readonly blastService: BlastService,
    private readonly userService: UtilisateurService,
    private readonly deliverieService: DeliveriesService, 
    private readonly fb: FormBuilder,
    private readonly holeService: HoleService,
    private readonly fonctionsService: FonctionsService,
    private readonly destroyRef: DestroyRef

  ) {
  }

  async ngOnInit(): Promise<void> {
    this.currentSiteId.set(this.userService.currentSite()?.siteId);
    this.holesDeliveryProxy.set(this.jsonDelivery.holesDelivery.map(hole => ({
      hole,
      selected: false
    })));

    this.allHoles.set(this.jsonDelivery.holesDelivery);
    this.currentLoadedCount.set(Math.min(this.pageSize(), this.allHoles().length));
    this.loadInitialHoles();

    await this.getDeliveryParams(this.currentSiteId());
    this.initForm(this.jsonDelivery);
    this.subscriteToStartTimeChange();
  }

  toggleReaffectDelivery(): void {
    this.reaffectDeliveryToggled.update(v => !v);
    if (this.reaffectDeliveryToggled()) {
      this.splitDeliveryToggled.set(false);
    }
  }

  toggleSplitDelivery(): void {
    this.splitDeliveryToggled.update(v => !v);
    if (this.splitDeliveryToggled()) {
      this.reaffectDeliveryToggled.set(false);
    }
  }

  protected onDestBlastIdChange(blastId: number) {
    this.blastService.getBlast(blastId).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((res) => {
      console.log(res);
      if (this.currentSiteId() == res.siteId) {
        this.destBlastname.set(res.nom);
      }
      else {
        this.destBlastname.set('wrong site');
      }
    },
      (error) => {
        this.destBlastname.set('not found');
      });
    ;
  }

  protected moveDelivery(destBlastId: number): void {
    this.deliverieService.MoveDelivery(this.delivery.deliveryId, destBlastId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.onClose.next(true);
        this.bsModalRef.hide();
        this.fonctionsService.launch_toast(true);
      },
        (error) => {
          console.log(error);
          this.fonctionsService.launch_toast(false);

        });
  }

  setTab(tab: string): void {
    this.selectedTab.set(tab);
  }

  getLocalDateAndTime(date: Date | string): { date: string, time: string } {
    const d = new Date(date);
    let annee = d.getFullYear();
    if (annee === 0) {
      annee = 1;
    }
    const year = annee.toString().padStart(4, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const hours = d.getHours().toString().padStart(2, '0');
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const seconds = d.getSeconds().toString().padStart(2, '0');

    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}:${seconds}`
    };
  }


  initForm(delivery?: JsonDelivery) {
    this.modifyLoadForm = this.fb.group({});
    
    // Handle drilling machine array - filter out invalid values
    let drillingMachine = delivery.drillingMachine.trim().slice(1, -1).trim().split(', ')
      .map(Number)
      .filter(id => id > 0); // Remove 0 or invalid IDs
    
    // Handle truck array - filter out invalid values  
    let truck = delivery.memuId.trim().slice(1, -1).trim().split(', ')
      .map(Number)
      .filter(id => id > 0); // Remove 0 or invalid IDs
    
    const start = this.getLocalDateAndTime(this.delivery.startTime);
    let startTime = start.time;
    let startDate = start.date;
    const end = this.getLocalDateAndTime(this.delivery.endTime);
    let endDate = end.date;
    let endTime = end.time;
    const departure = this.getLocalDateAndTime(delivery.departureTime);
    let departureDate = departure.date;
    let departureTime = departure.time;

    const deliveryForm = this.fb.group({
      blastId: [this.delivery.tirId],
      guid: [delivery.guid],
      customer: [delivery.client],
      deliveryName: [delivery.deliveryName],
      boutefeu: [delivery.blaster],
      operators: [delivery.operators],
      team: [delivery.teamId],
      truck: [truck.length > 0 ? truck : null],
      drill: [drillingMachine.length > 0 ? drillingMachine : null],
      holeDiameter: [delivery.holeDiameter],
      formulation: [delivery.formulation],
      kiloTotal: [{value: this.delivery.kgDelivered, disabled: true}],
      departureDate: [departureDate],
      departureTime: [departureTime],
      startDate: [startDate],
      startTime: [startTime],
      endDate: [endDate],
      endTime: [endTime],
      destBlastId: [''],
      bulkId:[delivery.bulkId]
    }, { validators: this.dateRangeValidator() });

    this.modifyLoadForm.addControl('deliveryForm', deliveryForm);
    this.modifyLoadForm.addControl('holesDeliveryForm', this.initializeHoleFormArray(delivery?.holesDelivery));
  }

  async getDeliveryParams(siteId: number): Promise<void> {
    this.holeService.getDeliveryParams(siteId).then(
      (response) => {
        this.deliveryParams.set(response);
      }
    ).catch(
      (error) => {
        console.error('Erreur:', error);
      }
    );
  }

  private initializeHoleFormArray(holesDelivery?: JsonDeliveryHole[]): FormArray {
    const holeDeliveryControl: FormArray = this.fb.array([]);
    
    if (this.visibleHoles() && this.visibleHoles().length > 0) {
      this.visibleHoles().forEach(hole => {
        const holeFormGroup = this.createHoleFormGroup(hole);
        holeDeliveryControl.push(holeFormGroup);
      });
    } else if (holesDelivery) {
      const initialHoles = holesDelivery.slice(0, this.pageSize());
      initialHoles.forEach(hole => {
        const holeFormGroup = this.createHoleFormGroup(hole);
        holeDeliveryControl.push(holeFormGroup);
      });
    }
    
    return holeDeliveryControl;
  }

  private readonly modifiedHolesData: Map<string, any> = new Map();

  protected loadInitialHoles(): void {
    this.visibleHoles.set(this.allHoles().slice(0, this.currentLoadedCount()));
  }

  protected loadMoreHoles(): void {
    if (this.currentLoadedCount() < this.allHoles().length && !this.isLoading()) {
      this.isLoading.set(true);
      
        const nextBatchSize = Math.min(this.pageSize(), this.allHoles().length - this.currentLoadedCount());
        const nextBatch = this.allHoles().slice(this.currentLoadedCount(), this.currentLoadedCount() + nextBatchSize);
        
        this.visibleHoles.set([...this.visibleHoles(), ...nextBatch]);
        this.currentLoadedCount.update(c => c + nextBatchSize);
        
        this.addHolesToForm(nextBatch);
        
        this.isLoading.set(false);
    }
  }

  protected onScroll(event: Event): void {
  if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
  this.scrollTimeout = setTimeout(() => {
    const el = event.target as HTMLElement;
    const threshold = 200;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - threshold) {
      this.loadMoreHoles();
    }
  }, 100);
}

  private addHolesToForm(newHoles?: JsonDeliveryHole[]): void {
    const holesFormArray = this.modifyLoadForm.get('holesDeliveryForm') as FormArray;
    const holesToAdd = newHoles || this.visibleHoles().slice(holesFormArray.length);
    
    holesToAdd.forEach((hole) => {
      const holeFormGroup = this.createHoleFormGroup(hole);
      
      if (this.modifiedHolesData.has(hole.guid)) {
        const savedData = this.modifiedHolesData.get(hole.guid);
        holeFormGroup.patchValue(savedData);
        holeFormGroup.markAsDirty();
      }
      
      holesFormArray.push(holeFormGroup);
    });
  }

  private saveCurrentModifications(): void {
    const holesFormArray = this.modifyLoadForm.get('holesDeliveryForm') as FormArray;
    
    holesFormArray.controls.forEach((control, index) => {
      if (control.dirty) {
        const holeGuid = this.visibleHoles()[index]?.guid;
        if (holeGuid) {
          this.modifiedHolesData.set(holeGuid, {
            ...control.value,
            index: index
          });
        }
      }
    });
  }


  private initializeDetoFormArray(detos?: JsonDeliveryDeto[]): FormArray {
    const detoControl: FormArray = this.fb.array([]);
    if (detos) {
      detos.forEach(deto => {
        const detoFormGroup = this.createDetoFormGroup(deto);
        detoControl.push(detoFormGroup);
      });
    }
    return detoControl;
  }

  private createHoleFormGroup(holeDelivery: JsonDeliveryHole): FormGroup {
    let holeCondition: HoleCondition[] = (holeDelivery?.holeCondition || [])
      .map(item => {
        return {
          id: Number(item.split('=')[0]),
          shortName: item.split('=')[1],
        };
      })
      .filter(condition => condition.shortName);

    const holeFormGroup = this.fb.group({
      guid: [holeDelivery.guid],
      holeName: [holeDelivery.holeId],
      holeId: [holeDelivery?.holeId],
      holeCondition: [holeCondition],
      comment: [holeDelivery?.comment],
      drilledDepth: [holeDelivery?.drilledDepth],
      measuredDepth: [holeDelivery?.measuredDepth],
      finalStemmingDepth: [holeDelivery?.finalStemmingDepth],
      kgSend: [holeDelivery?.kgSend],
      booster: [holeDelivery?.booster],
      boosterId: [holeDelivery?.boosterProductId],
      cartdridge: [holeDelivery?.cartdridge],
      cartridgeId: [holeDelivery?.cartdridgeId],
      detos: this.initializeDetoFormArray(holeDelivery?.detos),
      selectedForSplit: [false],
      bulkId: [holeDelivery?.bulkId],
      holeGuid: [holeDelivery?.holeGuid]

    });
    return holeFormGroup
  }

  private createDetoFormGroup(deto: JsonDeliveryDeto): FormGroup {
    const detoFormGroup = this.fb.group({
      typeDeto: [deto?.type],
      count: [deto?.count],
      productId: [deto?.productId],
      commercialName: [this.deliveryParams()?.detonators.find(d => d.id == deto.productId)?.commercialName],
    });
    return detoFormGroup;
  }

  submit(calledFromHolleDelivery?: boolean) {
    this.updateDelivery(this.jsonDelivery.guid, this.delivery.tirId, calledFromHolleDelivery);
  }

  updateDelivery(deliveryId: string, blastId: number, calledFromHolleDelivery = false) {
    let updateRequest = this.mapFormToDeliveryDto();
    if (updateRequest != null) {
      this.deliverieService.updateDelivery(deliveryId, blastId, updateRequest)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
        (res) => {
          if (!calledFromHolleDelivery) {
            this.onClose.next(true);
            this.bsModalRef.hide();
            this.fonctionsService.launch_toast(true);
          }
        },
        (error) => {
          this.fonctionsService.launch_toast(false);
          console.error('Error updating delivery:', error);
        });
    }
  }

  private isHoleModified(currentHole: any, index: number): boolean {
    const holeFormArray = this.modifyLoadForm.get('holesDeliveryForm') as FormArray;
    const holeFormGroup = holeFormArray.at(index);
    
    if (!holeFormGroup) {
      return false;
    }
    
    const isDirty = holeFormGroup.dirty;    
    return isDirty;
  }

  mapFormToDeliveryDto(): UpdateDelivery {
    const deliveryForm = this.modifyLoadForm.get('deliveryForm')?.value;
    
    this.saveCurrentModifications();
    
    const modifiedHoles = Array.from(this.modifiedHolesData.values())
      .map((holeData: any) => {
        const quantities = [];

        quantities.push({
          productId: deliveryForm.bulkId,
          productType: ProductType.Bulk,
          quantity: holeData.kgSend,
        });

        quantities.push({
          productId: holeData.cartridgeId,
          productType: ProductType.Cartridge,
          quantity: holeData.cartdridge,
        });

        quantities.push({
          productId: holeData.boosterId,
          productType: ProductType.Booster,
          quantity: holeData.booster,
        });

        for (const deto of holeData.detos || []) {
          quantities.push({
            productId: deto.productId,
            productType: ProductType.Detonator,
            quantity: deto.count,
          });
        }
        
        let holeDelivery: UpdateHoleDelivery = {
          deliveryId: deliveryForm.guid,
          holeId: holeData.holeGuid,
          holeDeliveryExternalGuid: holeData.guid,
          lengthMeasured: holeData.measuredDepth,
          lengthDrilled: holeData.drilledDepth,
          finalStemmingHeight: holeData.finalStemmingDepth,
          holeConditionIdList: (holeData.holeCondition || []).map((condition: HoleCondition) => `${condition.id}=${condition.shortName}`).join(';'),
          userComment: holeData.comment,
          holeName: holeData.holeId,
          quantities: quantities,
          holeConditions: holeData.holeCondition || [],
        };
        return holeDelivery;
      });

    const deliveryDto: UpdateDelivery = {
      blastId: deliveryForm.blastId,
      siteId: this.currentSiteId(),
      deliveryExternalId: deliveryForm.guid,
      name: deliveryForm.deliveryName,
      client: deliveryForm.customer,
      holeDiameter: deliveryForm.holeDiameter,
      drillMachine: `[ ${deliveryForm.drill?.join(', ')} ]`,
      startTime: new Date(`${deliveryForm.startDate}T${deliveryForm.startTime}`),
      endTime: new Date(`${deliveryForm.endDate}T${deliveryForm.endTime}`),
      departureTime: new Date(`${deliveryForm.departureDate}T${deliveryForm.departureTime}`),
      memuIdentifier: `[ ${deliveryForm.truck?.join(', ')} ]`,
      blasterName: deliveryForm.boutefeu?.toString(),
      operators: deliveryForm.operators,
      productFormulation: deliveryForm.formulation,
      teamId: deliveryForm.team,
      holeDeliveries: modifiedHoles,
    };
    return deliveryDto;
  }


  protected splitDelivery(destDeliveryName: string) {
    this.saveCurrentModifications();
    
    const currentSelected = this.modifyLoadForm.get('holesDeliveryForm')?.value
      .filter((hole: any) => hole.selectedForSplit)
      .map(hole => hole.guid) || [];
    
    const savedSelected = Array.from(this.modifiedHolesData.values())
      .filter((holeData: any) => holeData.selectedForSplit)
      .map(holeData => holeData.guid);
    
    const allSelectedGuids = [...new Set([...currentSelected, ...savedSelected])];
    
    this.deliverieService.SplitDelivery(this.delivery.deliveryId, destDeliveryName, allSelectedGuids)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        console.log(res);
        this.onClose.next(true);
        this.bsModalRef.hide();
        this.fonctionsService.launch_toast(true);
      },
        (error) => {
          console.log(error);
          this.fonctionsService.launch_toast(false);
        });
  }

  close() {
    this.onClose.next(true);
    this.bsModalRef.hide();
  }

  protected dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const startDateString = control.get('startDate')?.value + 'T' + control.get('startTime')?.value;
      const endDateString = control.get('endDate')?.value + 'T' + control.get('endTime')?.value;

      const startDate = new Date(startDateString);
      const endDate = new Date(endDateString);

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime()) || (startDate > endDate)) {
        return { 'dateInvalid': ' Invalid date or time range.' };
      }

      return null;
    };
  }

  private subscriteToStartTimeChange() {
    const deliveryFormGroup = this.modifyLoadForm.get('deliveryForm');
    if (deliveryFormGroup) {
      deliveryFormGroup.get('startTime')?.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((rawStartTime: string) => {
        const currentName = deliveryFormGroup.get('deliveryName')?.value ?? '';
        const [hours = '00', minutes = '00'] = rawStartTime.split(':');
        const newStartTime = `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
        let newName: string;
        if (/(\d{1,2}:\d{1,2})$/.test(currentName)) {
          newName = currentName.replace(/(\d{1,2}:\d{1,2})$/, newStartTime);
        } else {
          newName = `${currentName}_${newStartTime}`;
        }
        deliveryFormGroup.get('deliveryName')?.setValue(newName, { emitEvent: false });
      });
    }
  }

  protected mergeDeliveries(mergeEvent: { sourceId: string, destinationId: string }): void {
    this.onClose.next(true);
    this.bsModalRef.hide();
  }

}



